/**
 * OrbUniverseSigninPage (/u/:universe_id/signin)
 *
 * Called by ORB's iframe with ?ticket=<JWT>. Verifies the ticket with
 * our backend, receives an Uplift Bearer token, stores it (both for
 * same-tab navigation AND posts it to the parent window via
 * postMessage so ORB can survive iframe reloads), then redirects the
 * user into the app.
 *
 * postMessage protocol (to parent):
 *   { type: 'READY' }                     — on mount
 *   { type: 'UPLIFT_AUTH', token, exp }   — after successful verify
 *   { type: 'HEARTBEAT', ts }             — every 30s
 *   { type: 'RESIZE', height }            — whenever content height changes
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ShieldAlert } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// iter173 — explicit parent-origin allow-list. Replaces the prior
// `postMessage(msg, '*')` which would broadcast to ANY iframe parent.
// `REACT_APP_ORB_PARENT_ORIGIN` is the canonical parent (e.g.
// "https://www.collectiveorb.com"). We additionally accept
// `window.location.origin` so dev preview still works when the
// SPA hosts itself in an iframe of itself. If neither matches the
// actual parent, we skip the postMessage rather than broadcast.
const _ALLOWED_PARENT_ORIGINS = [
  process.env.REACT_APP_ORB_PARENT_ORIGIN || 'https://www.collectiveorb.com',
  window.location.origin,
].filter(Boolean);

// Module-scoped dedup — survives React StrictMode's dev double-mount,
// which unmounts and remounts the component (resetting any refs inside
// it). Maps ticket → in-flight Promise so a re-mount gets the same
// verify-ticket result the first mount already started.
const TICKET_PROMISES = new Map();

const post = (msg) => {
  try {
    if (window.parent && window.parent !== window) {
      // We don't have a reliable way to read window.parent.origin
      // cross-origin, so we post once per allowed origin. Origins
      // that don't match the actual parent are silently dropped by
      // the browser — never reach a third party.
      for (const origin of _ALLOWED_PARENT_ORIGINS) {
        try { window.parent.postMessage(msg, origin); } catch { /* parent closed */ }
      }
    }
  } catch { /* parent closed */ }
};

export default function OrbUniverseSigninPage() {
  const { universe_id: universeId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [status, setStatus] = useState('verifying'); // 'verifying' | 'error' | 'done'
  const [errMsg, setErrMsg] = useState('');
  const hbRef = useRef(null);

  useEffect(() => {
    post({ type: 'READY', universe_id: universeId });
    const ticket = params.get('ticket');
    if (!ticket) {
      setStatus('error');
      setErrMsg('Missing ticket — your ORB community session may have expired. Please re-open Operate from ORB.');
      return;
    }
    let cancelled = false;
    // Dedup per-ticket at module scope so React 18 StrictMode's
    // unmount-remount cycle doesn't fire a second verify-ticket (the
    // server correctly rejects a replay, but we don't want the user
    // to see "ticket already used" when the first call succeeded).
    let promise = TICKET_PROMISES.get(ticket);
    if (!promise) {
      promise = axios.post(`${API}/external/orb/verify-ticket`, { ticket })
        .then((r) => r.data);
      TICKET_PROMISES.set(ticket, promise);
    }
    (async () => {
      try {
        const data = await promise;
        if (cancelled) return;
        const { access_token, expires_in, user: ticketUser } = data || {};
        if (!access_token) throw new Error('no token in response');
        // Hydrate AuthContext synchronously so ProtectedRoute sees an
        // authenticated session BEFORE we navigate — otherwise the
        // next route redirects to /auth (the "Sign In / Sign Up" form
        // ORB was seeing). See ORB integration bug report 2026-05.
        await loginWithToken(access_token, ticketUser || null);
        post({
          type: 'UPLIFT_AUTH',
          token: access_token,
          exp: Math.floor(Date.now() / 1000) + (expires_in || 3600),
          universe_id: universeId,
        });
        setStatus('done');
        navigate('/', { replace: true });
      } catch (e) {
        if (cancelled) return;
        const detail = e?.response?.data?.detail || e.message || 'Unknown error';
        setStatus('error');
        setErrMsg(detail);
      }
    })();
    return () => { cancelled = true; };
  }, [params, universeId, navigate, loginWithToken]);

  // Heartbeat so ORB can detect a dead iframe.
  useEffect(() => {
    hbRef.current = setInterval(() => {
      post({ type: 'HEARTBEAT', ts: Date.now() });
    }, 30000);
    return () => clearInterval(hbRef.current);
  }, []);

  // Resize observer — ORB sizes the iframe to our content.
  useEffect(() => {
    const body = document.body;
    const ro = new ResizeObserver(() => {
      post({ type: 'RESIZE', height: body.scrollHeight });
    });
    ro.observe(body);
    return () => ro.disconnect();
  }, []);

  if (status === 'verifying') {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center p-6"
        data-testid="orb-signin-verifying"
        style={{ background: '#FAFAF7' }}
      >
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#809A75' }} />
        <p className="mt-3 text-sm text-muted-foreground">Connecting to your ORB community…</p>
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center p-6 text-center"
        data-testid="orb-signin-error"
        style={{ background: '#FAFAF7' }}
      >
        <ShieldAlert className="w-10 h-10 text-red-500 mb-2" />
        <p className="text-sm font-semibold">We couldn't sign you in</p>
        <p className="text-xs text-muted-foreground mt-1 max-w-sm">{errMsg}</p>
      </div>
    );
  }
  return null;
}
